'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/** Default duration for the tick-up animation in ms. */
const DEFAULT_DURATION_MS = 800;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnimatedNumberProps {
  /** The target numeric value to animate towards. */
  value: number;
  /**
   * Receives the current animated numeric value and returns the formatted
   * display content — a plain string (the fast path), or JSX when part of
   * the number needs its own styling (e.g. smaller fractional digits).
   * Called on every animation frame.
   */
  format: (value: number) => ReactNode;
  /** Animation duration in ms. @default 800 */
  duration?: number;
  /**
   * When true, the number ticks up from 0 on mount.
   * @default false
   */
  animateOnMount?: boolean;
  /**
   * When true, each value change briefly flashes the text colour — green
   * (`text-data-profit`) when the value increases, red (`text-data-loss`) when
   * it decreases — then fades back to the inherited colour. Used by live
   * feeds (e.g. the Winners feed price) to signal direction on each tick.
   * Suppressed under `prefers-reduced-motion`.
   * @default false
   */
  animateWithColour?: boolean;
  /** Additional class names applied to the outer `<span>`. */
  className?: string;
  /** Inline style applied to the outer `<span>` — used by callers needing
   *  dynamic colour (e.g. outcome-hue step vars not expressible as Tailwind). */
  style?: CSSProperties;
  /** Forwarded `data-testid` so callers don't need a wrapper span just to
   *  attach an identifier. */
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Ease-out cubic: fast start, decelerating finish — feels like a ticker
// that races then settles.
// ---------------------------------------------------------------------------

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function startAnimationFrameLoop(paintFrame: (now: number) => boolean): () => void {
  let frameId: number | null = null;
  const tick = (now: number): void => {
    if (paintFrame(now)) frameId = requestAnimationFrame(tick);
    else frameId = null;
  };
  frameId = requestAnimationFrame(tick);
  return (): void => {
    if (frameId !== null) cancelAnimationFrame(frameId);
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Animates a numeric value with a tick-up/down effect using
 * requestAnimationFrame. The `format` callback is invoked each frame
 * with the intermediate value so the consumer controls formatting.
 *
 * PERF — the tween does NOT re-render React per frame. When `format` returns
 * a string (the overwhelmingly common case — currency, volume, percentages),
 * each frame writes the `<span>`'s `textContent` directly; React state is
 * touched only once, at settle, to keep its tree consistent. A ~800ms tween
 * therefore costs 1 React render instead of ~48. `format`s that return JSX
 * fall back to per-frame `setState` (React must reconcile the element tree).
 *
 * Set `animateOnMount` to tick up from 0 when the component first renders.
 * Respects `prefers-reduced-motion` — snaps instantly.
 */
export function AnimatedNumber({
  value,
  format,
  duration = DEFAULT_DURATION_MS,
  animateOnMount = false,
  animateWithColour = false,
  className,
  style,
  ...rest
}: AnimatedNumberProps): React.ReactNode {
  const testId = rest['data-testid'];
  const initialNumeric = animateOnMount ? 0 : value;
  // `display` is the React-rendered content. For string formats it is only
  // refreshed at settle (the tween writes the DOM node directly); for
  // non-string (JSX) formats it updates per frame.
  const [display, setDisplay] = useState<ReactNode>(() => format(initialNumeric));
  // Tracks the actual displayed numeric value — NOT the previous target. When
  // a new `value` arrives mid-animation, the next tween starts from where the
  // user is currently looking, instead of snapping to the old target first.
  const currentRef = useRef(initialNumeric);
  const spanRef = useRef<HTMLSpanElement | null>(null);
  // The last string the tween wrote to the DOM. Used to re-assert the node's
  // text after an unrelated parent-driven re-render — which would otherwise
  // reconcile the span back to the stale `display` state for one frame.
  // `null` when no string tween is in effect (the `display` state is truth).
  const lastStringRef = useRef<string | null>(null);
  // Direction-flash state for `animateWithColour`. `null` ⇒ inherited colour.
  const [flashDir, setFlashDir] = useState<'up' | 'down' | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const from = currentRef.current;
    const to = value;

    if (from === to) {
      lastStringRef.current = null;
      setDisplay(format(to));
      return;
    }

    // Respect reduced-motion preference.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      currentRef.current = to;
      lastStringRef.current = null;
      setDisplay(format(to));
      return;
    }

    // Direction flash — fade to success/destructive then back. Skipped under
    // reduced-motion (the early return above already handled that case).
    if (animateWithColour) {
      setFlashDir(to > from ? 'up' : 'down');
      if (flashTimerRef.current !== null) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashDir(null), 700);
    }

    const diff = to - from;
    const start = performance.now();

    /**
     * Render one frame. String/number output is written straight to the DOM
     * node (no React render); only `settle` syncs React state. JSX output
     * falls back to `setState`.
     */
    function paint(numeric: number, settle: boolean): void {
      const out = format(numeric);
      if (typeof out === 'string' || typeof out === 'number') {
        const str = String(out);
        lastStringRef.current = str;
        // Rounded percentages often stay identical across many animation frames.
        if (spanRef.current !== null && spanRef.current.textContent !== str) {
          spanRef.current.textContent = str;
        }
        if (settle) {
          // Sync React state to the final value so a later parent re-render
          // reconciles the span to the correct content instead of clobbering
          // the directly-written text with stale state.
          setDisplay(str);
        }
      } else {
        lastStringRef.current = null;
        setDisplay(out);
      }
    }

    function paintFrame(): boolean {
      // A shared rAF timestamp can predate this effect on a busy frame.
      // Read the same monotonic clock used for start to avoid negative easing.
      const elapsed = performance.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const current = from + diff * easeOutCubic(progress);

      currentRef.current = current;

      if (progress < 1) {
        paint(current, false);
        return true;
      } else {
        currentRef.current = to;
        paint(to, true);
        return false;
      }
    }

    return startAnimationFrameLoop(paintFrame);
  }, [value, duration, format, animateWithColour]);

  // Clear the flash timer on unmount.
  useEffect(
    () => (): void => {
      if (flashTimerRef.current !== null) clearTimeout(flashTimerRef.current);
    },
    [],
  );

  // Re-assert the DOM text after any render while a string tween's value is
  // live — keeps the directly-written number from being clobbered by a stale
  // `display` reconcile. No-op once `display` and the tween agree (settle).
  useLayoutEffect(() => {
    const str = lastStringRef.current;
    if (str !== null && spanRef.current !== null && spanRef.current.textContent !== str) {
      spanRef.current.textContent = str;
    }
  });

  return (
    <span
      ref={spanRef}
      className={cn(
        'font-sans tabular-nums',
        animateWithColour && 'transition-colors duration-slow',
        flashDir === 'up' && 'text-data-profit',
        flashDir === 'down' && 'text-data-loss',
        className,
      )}
      {...(style !== undefined ? { style } : {})}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {display}
    </span>
  );
}
